<template>
  <section class="tours">
    <transition name="transition-fade">
      <div
        v-if="isLoading"
        class="tours__spinner"
      >
        <c-loader-spinner />
      </div>
    </transition>

    <header class="tours__header">
      <c-headline
        class="mb-sm"
        level="2"
      >
        {{ getLabel('introduction.tours_select_head') }}
      </c-headline>

      <div
        class="tours__introduction"
        v-html="getLabel(`introduction.tours_${tours.length ? 'select' : 'empty'}`)"
      />

      <section class="tours__filter">
        <c-form-select
          v-model="tourFilter"
          color="neutral"
          inline
          inline-label
        >
          {{ $t('tourFilter') }}:
          <template #options>
            <option
              v-for="[filter, text] in Object.entries($t('tourFilters'))"
              :key="filter"
              :value="filter"
            >
              {{ text }}
            </option>
          </template>
        </c-form-select>
      </section>
    </header>

    <c-tours-item
      v-for="tour in filteredTours"
      :key="tour.gap"
      :gap="tour.gap"
      :time="tour.von_zeit"
      :name="tour[`name_${$i18n.locale}`]"
      :description="tour[`spezifische_beschreibung_${$i18n.locale}`]"
      :free-spots="tour.fuehrung_frei"
      :sold-spots="tour.fuehrung_verkauft"
      :price-groups="tour.preisgruppen"
      :price-deviation="tour.preisabweichung"
      :basetype="tour.experience_basetype"
      :typ="tour.experience_type"
      :cls="tour.experience_class"
    />
  </section>
</template>

<script>
import CFormSelect from './formSelect.vue'
import CHeadline from './headline.vue'
import CLoaderSpinner from './loaderSpinner.vue'
import CToursItem from './CToursItem.vue'

import eventBus from '../eventBus.js'

import labelMixin from '../mixins/label.js'

export default {
  name: 'CTours',

  components: {
    CFormSelect,
    CHeadline,
    CLoaderSpinner,
    CToursItem
  },

  mixins: [labelMixin],

  data () {
    return {
      isLoading: true
    }
  },

  computed: {
    tourFilter: {
      get () {
        return this.$store.state.tourFilter
      },
      set (value) {
        this.$store.commit('setTourFilter', parseInt(value))
      }
    },
    filteredTours () {
      return this.$store.getters.filteredTours
    },
    tours () {
      return this.$store.state.tours
    },
    date () {
      return this.$store.state.ticketSelection.selectedDate
    },
    cart () {
      return this.$store.state.cart.tours
    }
  },

  watch: {
    date () {
      this.getTours()
    },
    cart () {
      this.getTours()
    }
  },

  created () {
    // This will be executed if the user comes from the previous booking view
    // and a date has already been set. Date changes and direct routing will be
    // handled by the watcher. It's counter-intuitive, but this app doesn't have
    // any defined data flow at all. So we need to be creative.
    if (this.date) {
      this.getTours()
    }
  },

  methods: {
    async getTours () {
      this.isLoading = true

      try {
        await this.$store.dispatch('getTours', { date: this.date })
      } catch (error) {
        eventBus.emit('handleError', error.response.data)
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<i18n locale="en">
{
  "tourFilter": "Filter by experience type",
  "tourFilters": {
    "0": "All experiences",
    "1": "Only tours",
    "2": "Only VR experiences",
    "3": "Only small VR experiences",
    "4": "Only big VR experiences"
  }
}
</i18n>

<i18n locale="de">
{
  "tourFilter": "Nach Erlebnis-Art filtern",
  "tourFilters": {
    "0": "Alle",
    "1": "Nur Führungen",
    "2": "Nur VR-Erlebnisse",
    "3": "Nur kleines VR-Erlebnis",
    "4": "Nur großes VR-Erlebnis"
  }
}
</i18n>

<style lang="scss">
@import "../css/variables";

.tours {
  position: relative;
  min-height: 4em;

  background: #fff;
}

.tours__spinner {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  padding: 1em;

  text-align: center;

  background: hsl(0deg 100% 100% / 0.8);
}

.tours__header {
  padding: $gutter-sm;

  @media (min-width: $breakpoint-md) {
    padding: $gutter-md;
  }
}

.tours__introduction {
  > :last-child {
    margin-bottom: 0;
  }
}

.tours__filter {
  padding-top: 1em;
}
</style>
